import { isEmptyString } from '@/utils/string';
import { generate, type CopilotSession } from '@customerio/hydra-copilot';
import { useCopilotMessages, useCopilotMeta, useCopilotStatus } from '@customerio/hydra-copilot/react';
import { useQuery } from '@tanstack/react-query';
import { z } from '@customerio/hydra-validation';

import { useCopilotActions } from './use-copilot-actions';

export const MAX_COPILOT_SESSION_NAME_LENGTH = 80;

/**
 * Automatically generates a name for a copilot session when it has no name,
 * isn't receiving a message, and has at least 2 messages.
 */
export function useCopilotSessionAutoName(session: CopilotSession) {
  const meta = useCopilotMeta(session);
  const messages = useCopilotMessages(session);
  const status = useCopilotStatus(session);
  const actions = useCopilotActions();

  useQuery({
    queryKey: ['copilotSessionAutoName', session.id],
    queryFn: async () => {
      const messages = session.__statefulMessages.get();
      if (messages.length < 2) {
        return null;
      }

      const { name } = await generate(
        `Generate a name for this chat based on the first two messages. The name should be short and descriptive, e.g. "Campaign deep dive". It will be trimmed after ${MAX_COPILOT_SESSION_NAME_LENGTH} characters.`,
        z.toJSONSchema(
          z.object({
            name: z.string().describe('The name of the chat').min(1).max(MAX_COPILOT_SESSION_NAME_LENGTH),
          }),
        ),
        {
          context: [
            {
              description: 'The messages in the session',
              value: messages.slice(0, 2),
            },
          ],
        },
      );

      const sessionId = session.id;
      actions.renameSession(Number(sessionId), name.trim()).then(() => {
        if (session.id === sessionId) {
          session.setSessionMeta({
            ...session.__meta.get(),
            name: name.trim(),
          });
        }
      });

      return name;
    },
    enabled: messages.length >= 2 && status === 'ready' && isEmptyString(meta.name),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
    retry: false,
    staleTime: Infinity,
  });
}
